import { Heading } from '@findy-code/yuffie/ui/heading';
import { Stack } from '@findy-code/yuffie/ui/stack';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { FaGithub } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

import { useExpandableItems } from '../../../common';
import styles from './company-detail-members.component.module.scss';
import { SectionLayout } from './section-layout.component';

type MemberType = {
  name: string;
  githubAccount?: string | null;
  jobPosition?: string | null;
  introduction?: string | null;
};

const MemberItem = (props: { member: MemberType }) => {
  const { member } = props;
  const imageUrl = member.githubAccount
    ? `https://avatars.githubusercontent.com/${member.githubAccount}`
    : '/images/user_default_icon.png';

  return (
    <div className={styles.memberItemBlock}>
      <Stack
        className={styles.memberItemInnerBlock}
        direction="row"
        spacing="xs"
      >
        <div className={styles.memberImageBlock}>
          <img
            alt={`${member.name}のアバター`}
            className={styles.memberImage}
            height="60"
            src={imageUrl}
            width="60"
          />
        </div>
        <div>
          <div className={styles.memberJobPosition}>{member.jobPosition}</div>
          <Heading as="h3" className={styles.memberName} font="heading-m">
            {member.name}
          </Heading>
          {member.githubAccount && (
            <div style={{ marginTop: '2px' }}>
              <a
                href={`https://github.com/${member.githubAccount}`}
                rel="noopener"
                target="_blank"
              >
                <FaGithub
                  aria-label="GitHub"
                  className={styles.githubIcon}
                  color="black"
                  size="15"
                />
                <span className={styles.githubName}>
                  {member.githubAccount}
                </span>
              </a>
            </div>
          )}
        </div>
      </Stack>
      {member.introduction && <div>{member.introduction}</div>}
    </div>
  );
};

const NextMember = (props: { companyId: number }) => {
  const { companyId } = props;

  return (
    <div className={styles.memberItemBlock}>
      <Link href={`/companies/${companyId}/jobs`}>
        <div className={styles.nextMemberIcon} />
        <div>
          <Heading as="h4" className={styles.memberName} font="heading-m">
            Join our team!
          </Heading>
          <span className={styles.githubName}>
            次のメンバーになりませんか？
          </span>
        </div>
      </Link>
    </div>
  );
};

type Props = {
  companyId?: number;
  members: MemberType[];
};

export const CompanyDetailMembers = ({ companyId, members }: Props) => {
  const isDisplayLgSize = useMediaQuery({ query: '(min-width: 1140px)' });

  const { displayItems, ExpandButton, isVisibleAllItems } = useExpandableItems(
    members,
    isDisplayLgSize ? 3 : 2,
    'メンバーをもっとみる'
  );

  // hydrate errorを回避するためにマウント後に表示する
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <SectionLayout heading={{ title: 'メンバー' }} id="member">
      <div className={styles.wrapper}>
        {isMounted ? (
          <>
            {displayItems.map((member, index) => (
              <MemberItem key={index} member={member} />
            ))}
            {(isVisibleAllItems ||
              members.length <= (isDisplayLgSize ? 2 : 1)) &&
              companyId && <NextMember companyId={companyId} />}
          </>
        ) : null}
      </div>
      <ExpandButton />
    </SectionLayout>
  );
};
